import React, { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, X } from 'lucide-react';
import { Button } from 'components/Button';
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from 'react-hook-form';
import api from 'services/api';
import { useTranslation } from 'react-i18next';
import { ReportType, ReportTypeValues } from 'models/report';
import { Group } from 'models/group';

const reportFormSchema = z.object({
  file: z
    .custom<FileList>((value) => value instanceof FileList && value.length > 0, {
      message: 'Um arquivo é obrigatório.',
    })
    .refine((files) => files[0]?.type === 'video/mp4', { // Aceita apenas VTT
      message: 'Apenas arquivos mp4 são permitidos.',
    }),
  type: z.nativeEnum(ReportType),
})

type ReportFormData = z.infer<typeof reportFormSchema>

interface ReportDialogProps {
    open: boolean,
    setOpen: (open:boolean) => void,
    onSubmit: () => void,
    group: Group
}
export function ReportDialog({open, group, setOpen, onSubmit}: ReportDialogProps) {
  const { 
      register, 
      handleSubmit,
      control,
      formState: { errors, isDirty } 
  } = useForm<ReportFormData>({
      resolver: zodResolver(reportFormSchema)
  });
  const { t } = useTranslation()
  
  const [sending, setSending] = useState(false);

  async function submit(data: ReportFormData) {
    setSending(true);
    try
    {
      const form = new FormData();
      form.append('file', data.file[0]);
      form.append('type', data.type.toString());
      
      console.log(form)
      await api.post(`/groups/${group?.id}/reports`, form);

      onSubmit();
    } catch (e:any) {
      if (e?.response?.data)
        window.alert(e.response.data);
    }
    setSending(false);
    setOpen(false);
  }

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="bg-black/50 data-[state=open]:animate-overlayShow fixed inset-0" />
            <Dialog.Content className="data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none overflow-hidden">
              <Dialog.Title className="text-mauve12 m-0 text-[17px] font-medium">
                {t("reportsDialog.newReport")}
              </Dialog.Title>
              <Dialog.Description className="text-mauve11 mt-[10px] mb-5 text-[15px] leading-normal">
                {t("reportsDialog.description")}
              </Dialog.Description>
              <form onSubmit={handleSubmit(submit)}>
              
              <fieldset className="mb-[15px] flex items-center gap-5">
                <label className="w-[90px] text-right text-[15px]" htmlFor="status">
                {t("reportsDialog.type")}
                </label>
                <select
                  className="border border-zinc-400 focus:border-black inline-flex h-[35px] w-full flex-1 items-center justify-center px-[10px] text-[15px] leading-none outline-none"
                  {...register("type", { valueAsNumber: true })}
                >
                  {ReportTypeValues.map((type, index) => (
                    <option key={index} value={index + 1}>
                      {t("reportType." + type)}
                    </option>
                  ))}
                </select>
              </fieldset>
              {errors?.type && <span className='text-red-600 w-full flex justify-end mb-4'>{errors?.type?.message}</span>}

              <fieldset className="mb-[15px] flex items-center gap-5">
                <label className="w-[90px] text-right text-[15px]" htmlFor="crm">
                {t("reportsDialog.file")}
                </label>
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="file"
                      accept=".mp4"
                      onChange={(e) => field.onChange(e.target.files)}
                    />
                  )}
                />
              </fieldset>
              {errors?.file && <span className='text-red-600 w-full flex justify-end mb-4'>{errors?.file?.message}</span>}


              <div className="mt-[25px] flex justify-end">
                <Button type='submit' disabled={!isDirty || sending}>
                  {sending ? t("reportsDialog.sending") : t("reportsDialog.send") }
                  {sending && <Loader2 className='animate-spin' size={16} />}
                </Button>
              </div>
              </form>
              <Dialog.Close asChild>
                <button
                  className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute top-[10px] right-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                  aria-label="Close"
                >
                  <X />
                </button>
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      );
}
